/**
 * OpenAI Ads (ChatGPT) Measurement Pixel helpers.
 * @see https://developers.openai.com/ads/measurement-pixel
 *
 * Amounts are ISO 4217 minor units (cents for USD) — same as Sharetribe Money.amount.
 */

const CHECKOUT_STARTED_KEY_PREFIX = 'oaiq_checkout_started:';
const CONTENTS_VIEWED_KEY_PREFIX = 'oaiq_contents_viewed:';

export const getOpenAIAdsPixelId = () =>
  String(process.env.REACT_APP_OPENAI_ADS_PIXEL_ID || '').trim();

export const isOpenAIAdsEnabled = () => !!getOpenAIAdsPixelId();

const safeSessionGet = key => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSessionSet = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    // ignore quota / private mode
  }
};

/**
 * @param {string} eventName
 * @param {object} eventData
 * @param {object} [options]
 */
export const measureOpenAIAdsEvent = (eventName, eventData, options) => {
  if (typeof window === 'undefined' || typeof window.oaiq !== 'function') {
    return;
  }
  try {
    if (options && typeof options === 'object') {
      window.oaiq('measure', eventName, eventData, options);
    } else {
      window.oaiq('measure', eventName, eventData);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[openai-ads] measure failed', eventName, e);
  }
};

const listingContentItem = (listing, extras = {}) => {
  const id = listing?.id?.uuid || listing?.id;
  if (!id) return null;
  const name = listing?.attributes?.title || undefined;
  return {
    id: String(id),
    ...(name ? { name: String(name) } : {}),
    content_type: 'product',
    ...extras,
  };
};

const moneyFieldsFromPayin = payin => {
  if (!payin || typeof payin.amount !== 'number' || !Number.isFinite(payin.amount)) {
    return {};
  }
  return {
    amount: Math.round(payin.amount),
    currency: payin.currency || 'USD',
  };
};

/**
 * SPA route change — OpenAI does not auto-fire page_viewed on history changes.
 */
export const trackOpenAIAdsPageViewed = (canonicalPath) => {
  const path = canonicalPath || (typeof window !== 'undefined' ? window.location?.pathname : '') || '/';
  measureOpenAIAdsEvent('page_viewed', {
    type: 'contents',
    contents: [
      {
        id: String(path),
        name: String(path),
        content_type: 'page',
      },
    ],
  });
};

/**
 * Event / listing detail view.
 * Deduped per listing per tab session so Strict Mode remounts don't double-count.
 */
export const trackOpenAIAdsContentsViewed = listing => {
  const item = listingContentItem(listing);
  if (!item) return;

  const key = `${CONTENTS_VIEWED_KEY_PREFIX}${item.id}`;
  if (safeSessionGet(key) === '1') return;
  safeSessionSet(key, '1');

  const price = listing?.attributes?.price;
  measureOpenAIAdsEvent('contents_viewed', {
    type: 'contents',
    ...moneyFieldsFromPayin(price),
    contents: [item],
  });
};

/**
 * Buyer entered checkout for an event (OrderPanel or Seatics lock → checkout).
 * @param {object} opts
 * @param {object} opts.listing
 * @param {object} [opts.orderData]
 * @param {object} [opts.payin] Money-like { amount, currency }
 * @param {string} [opts.eventId] optional override for dedupe key
 */
export const trackOpenAIAdsCheckoutStarted = ({ listing, orderData, payin, eventId } = {}) => {
  const item = listingContentItem(listing, {
    ...(Number.isInteger(Number(orderData?.quantity)) && Number(orderData.quantity) > 0
      ? { quantity: Math.round(Number(orderData.quantity)) }
      : {}),
  });
  if (!item) return;

  const dedupeId = eventId || item.id;
  const key = `${CHECKOUT_STARTED_KEY_PREFIX}${dedupeId}`;
  if (safeSessionGet(key) === '1') return;
  safeSessionSet(key, '1');

  const unitPrice = listing?.attributes?.price;
  const qty = Number(orderData?.quantity) > 0 ? Number(orderData.quantity) : 1;
  const inferredPayin =
    payin ||
    (unitPrice && typeof unitPrice.amount === 'number'
      ? { amount: unitPrice.amount * qty, currency: unitPrice.currency || 'USD' }
      : orderData?.tnSelection?.unitPrice
        ? {
            // TN unitPrice is dollars (all-in); convert to minor units.
            amount: Math.round(Number(orderData.tnSelection.unitPrice) * 100 * qty),
            currency: unitPrice?.currency || 'USD',
          }
        : null);

  measureOpenAIAdsEvent('checkout_started', {
    type: 'contents',
    ...moneyFieldsFromPayin(inferredPayin),
    contents: [item],
  });
};

/**
 * Purchase completed (Sharetribe transition/confirm-payment).
 * event_id = transaction UUID for browser ↔ Conversions API dedupe.
 *
 * @param {object} opts
 * @param {string|object} opts.orderId UUID string or { uuid }
 * @param {object} [opts.transaction] confirmed transaction (preferred for payinTotal)
 * @param {object} [opts.listing]
 * @param {object} [opts.orderData]
 */
export const trackOpenAIAdsOrderCreated = ({ orderId, transaction, listing, orderData } = {}) => {
  const id =
    typeof orderId === 'string'
      ? orderId
      : orderId?.uuid || transaction?.id?.uuid || transaction?.id;
  if (!id) return;

  const payin = transaction?.attributes?.payinTotal;
  const qty =
    Number(orderData?.quantity) > 0
      ? Math.round(Number(orderData.quantity))
      : undefined;
  const item = listingContentItem(listing, {
    ...(qty ? { quantity: qty } : {}),
    ...moneyFieldsFromPayin(payin),
  });

  measureOpenAIAdsEvent(
    'order_created',
    {
      type: 'contents',
      ...moneyFieldsFromPayin(payin),
      ...(item ? { contents: [item] } : {}),
    },
    { event_id: String(id) }
  );
};
